import { Box, Link, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';

type MediaKind = 'audio' | 'image' | 'unknown';

const audioExtensions = new Set(['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.oga', '.flac', '.webm']);
const imageExtensions = new Set(['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.svg', '.avif']);

function getPathname(url: string): string {
    try {
        return new URL(url).pathname.toLowerCase();
    } catch {
        return url.split('?')[0].toLowerCase();
    }
}

function hasExtension(pathname: string, extensions: Set<string>): boolean {
    return [...extensions].some((extension) => pathname.endsWith(extension));
}

export function getMediaKind(url: string): MediaKind {
    const pathname = getPathname(url);

    if (hasExtension(pathname, audioExtensions)) {
        return 'audio';
    }

    if (hasExtension(pathname, imageExtensions)) {
        return 'image';
    }

    return 'unknown';
}

type MediaPreviewProps = {
    url: string;
    title: string;
    width?: number | string;
    height?: number | string;
    showUrl?: boolean;
    showWaveform?: boolean;
    mediaKind?: MediaKind;
};

type AudioWaveformPreviewProps = {
    url: string;
    width: number | string;
    showWaveform: boolean;
};

function AudioWaveformPreview({
    url,
    width,
    showWaveform,
}: AudioWaveformPreviewProps) {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const waveformRef = useRef<HTMLDivElement | null>(null);
    const [waveformReady, setWaveformReady] = useState(false);
    const [waveformError, setWaveformError] = useState<string | null>(null);

    useEffect(() => {
        setWaveformReady(false);
        setWaveformError(null);

        if (!showWaveform || !audioRef.current || !waveformRef.current) {
            return undefined;
        }

        const audioElement = audioRef.current;

        const wavesurfer = WaveSurfer.create({
            container: waveformRef.current,
            media: audioElement,
            height: 64,
            waveColor: '#94a3b8',
            progressColor: '#1d4ed8',
            cursorColor: '#0f172a',
            barWidth: 2,
            barGap: 1,
            barRadius: 999,
            normalize: true,
        });

        const handleReady = () => {
            setWaveformReady(true);
            setWaveformError(null);
        };

        const handleWaveformError = (error: Error | string) => {
            const message = typeof error === 'string' ? error : error.message;
            setWaveformReady(false);
            setWaveformError(message);
        };

        const handleAudioError = () => {
            setWaveformReady(false);
            setWaveformError('Waveform preview is unavailable for this audio source.');
        };

        wavesurfer.on('ready', handleReady);
        wavesurfer.on('error', handleWaveformError);
        audioElement.addEventListener('error', handleAudioError);

        return () => {
            wavesurfer.un('ready', handleReady);
            wavesurfer.un('error', handleWaveformError);
            audioElement.removeEventListener('error', handleAudioError);
            wavesurfer.destroy();
        };
    }, [showWaveform, url]);

    return (
        <Stack spacing={1}>
            <Box
                component="audio"
                ref={audioRef}
                controls
                preload="metadata"
                src={url}
                sx={{ width: typeof width === 'number' ? `${width}px` : width, maxWidth: '100%' }}
            >
                Your browser does not support the audio element.
            </Box>
            {showWaveform ? (
                <Stack spacing={0.75}>
                    <Box
                        ref={waveformRef}
                        sx={{
                            width: typeof width === 'number' ? `${width}px` : width,
                            maxWidth: '100%',
                            minHeight: 64,
                            px: 1,
                            py: 0.75,
                            borderRadius: 1,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            backgroundColor: (theme) => theme.palette.background.default,
                            opacity: waveformReady ? 1 : 0.35,
                            display: waveformError ? 'none' : 'block',
                        }}
                    />
                    {waveformError ? (
                        <Typography variant="caption" color="text.secondary">
                            Waveform preview unavailable because this audio host blocks browser access to the WAV data.
                        </Typography>
                    ) : null}
                </Stack>
            ) : null}
        </Stack>
    );
}

export function MediaPreview({
    url,
    title,
    width = 120,
    height = 80,
    showUrl = false,
    showWaveform = false,
    mediaKind: mediaKindOverride,
}: MediaPreviewProps) {
    const mediaKind = mediaKindOverride ?? getMediaKind(url);

    return (
        <Stack spacing={1} sx={{ minWidth: 0 }}>
            {mediaKind === 'audio' ? (
                <AudioWaveformPreview url={url} width={width} showWaveform={showWaveform} />
            ) : mediaKind === 'image' ? (
                <Box
                    component="img"
                    src={url}
                    alt={title}
                    sx={{
                        display: 'block',
                        width,
                        height,
                        objectFit: 'contain',
                        objectPosition: 'center',
                        backgroundColor: '#0f172a',
                        borderRadius: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        maxWidth: '100%',
                    }}
                />
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Preview unavailable
                </Typography>
            )}

            {showUrl ? (
                <Link
                    href={url}
                    underline="hover"
                    sx={{ wordBreak: 'break-all' }}
                >
                    {url}
                </Link>
            ) : null}
        </Stack>
    );
}
